import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, Users } from 'lucide-react';
import { api } from '../../api/client.js';

interface FamilyStepProps {
  onNext: () => void;
  onSkip: () => void;
}

type Mode = 'choose' | 'join' | 'create';

export function FamilyStep({ onNext, onSkip }: FamilyStepProps) {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>('choose');
  const [inviteCode, setInviteCode] = useState('');
  const [familyName, setFamilyName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleJoin = async () => {
    if (!inviteCode.trim()) return;
    setIsLoading(true);
    setError(null);

    const result = await api.joinFamily(inviteCode.trim());

    if (result.error) {
      setError(result.error.message);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    onNext();
  };

  const handleCreate = async () => {
    if (!familyName.trim()) return;
    setIsLoading(true);
    setError(null);

    const result = await api.createFamily({ name: familyName.trim() });

    if (result.error) {
      setError(result.error.message);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    onNext();
  };

  if (mode === 'choose') {
    return (
      <div className="max-w-sm mx-auto text-center">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/20 mx-auto mb-4">
          <KeyRound className="w-7 h-7 text-primary" />
        </div>

        <h2 className="text-xl font-bold text-ink-1 mb-2">
          {t('wizard.family.chooseTitle', 'Get started with a family')}
        </h2>
        <p className="text-ink-3 mb-6 text-sm">
          {t('wizard.family.chooseDesc', 'Create a new family or join one with an invite code from someone else.')}
        </p>

        <button
          onClick={() => { setMode('create'); }}
          className="w-full px-6 py-3 rounded-xl bg-primary hover:bg-primary-hover text-primary-on font-semibold transition-colors mb-3"
        >
          {t('wizard.family.createCta', 'Create a Family')}
        </button>

        <button
          onClick={() => { setMode('join'); }}
          className="w-full px-6 py-3 rounded-xl bg-surface-3 hover:bg-surface-2 border border-line-2 text-ink-1 font-semibold transition-colors mb-3"
        >
          {t('wizard.family.joinCta', 'Join Existing Family')}
        </button>

        <button
          onClick={onSkip}
          className="text-sm text-ink-3 hover:text-ink-2 transition-colors"
        >
          {t('wizard.family.skip')}
        </button>
      </div>
    );
  }

  if (mode === 'create') {
    return (
      <div className="max-w-sm mx-auto text-center">
        <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/20 mx-auto mb-4">
          <Users className="w-7 h-7 text-primary" />
        </div>

        <h2 className="text-xl font-bold text-ink-1 mb-2">
          {t('wizard.family.createTitle', 'Name your family')}
        </h2>
        <p className="text-ink-3 mb-6 text-sm">
          {t('wizard.family.createDesc', "What should we call this family? You can change it later.")}
        </p>

        {error && (
          <div className="mb-4 p-3 rounded-lg bg-danger/10 border border-red-500/20 text-danger text-sm text-left">
            {error}
          </div>
        )}

        <label className="block text-sm text-ink-3 mb-1 text-left">
          {t('wizard.family.nameLabel', 'Family name')}
        </label>
        <input
          type="text"
          value={familyName}
          onChange={(e) => { setFamilyName(e.target.value); }}
          onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
          placeholder={t('wizard.family.namePlaceholder', 'The Murphys')}
          className="w-full px-4 py-3 rounded-xl bg-surface-3 border border-line-2 text-ink-1 placeholder-ink-4 focus:outline-none focus:border-primary mb-4"
          maxLength={50}
          autoFocus
        />

        <button
          onClick={handleCreate}
          disabled={!familyName.trim() || isLoading}
          className="w-full px-6 py-3 rounded-xl bg-primary hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-primary-on font-semibold transition-colors mb-3"
        >
          {isLoading ? t('common.creating', 'Creating...') : t('wizard.family.createConfirm', 'Create Family')}
        </button>

        <button
          onClick={() => { setMode('choose'); setError(null); }}
          className="text-sm text-ink-3 hover:text-ink-2 transition-colors"
        >
          {t('common.back', 'Back')}
        </button>
      </div>
    );
  }

  // mode === 'join'
  return (
    <div className="max-w-sm mx-auto text-center">
      <div className="flex items-center justify-center w-14 h-14 rounded-full bg-primary/20 mx-auto mb-4">
        <KeyRound className="w-7 h-7 text-primary" />
      </div>

      <h2 className="text-xl font-bold text-ink-1 mb-2">
        {t('wizard.family.joinExisting')}
      </h2>
      <p className="text-ink-3 mb-6 text-sm">
        {t('wizard.family.joinDesc')}
      </p>

      {error && (
        <div className="mb-4 p-3 rounded-lg bg-danger/10 border border-red-500/20 text-danger text-sm text-left">
          {error}
        </div>
      )}

      <label className="block text-sm text-ink-3 mb-1 text-left">
        {t('wizard.family.inviteCode')}
      </label>
      <input
        type="text"
        name="chlann-family-invite-code"
        value={inviteCode}
        onChange={(e) => { setInviteCode(e.target.value.toUpperCase()); }}
        onKeyDown={(e) => e.key === 'Enter' && handleJoin()}
        placeholder={t('wizard.family.inviteCodePlaceholder')}
        className="w-full px-4 py-3 rounded-xl bg-surface-3 border border-line-2 text-ink-1 placeholder-ink-4 focus:outline-none focus:border-primary mb-4 text-center font-mono text-lg tracking-widest uppercase"
        maxLength={50}
        autoComplete="off"
        autoFocus
        data-1p-ignore
        data-lpignore="true"
        data-form-type="other"
      />

      <button
        onClick={handleJoin}
        disabled={!inviteCode.trim() || isLoading}
        className="w-full px-6 py-3 rounded-xl bg-primary hover:bg-primary-hover disabled:opacity-50 disabled:cursor-not-allowed text-primary-on font-semibold transition-colors mb-3"
      >
        {isLoading ? t('family.joining') : t('family.join')}
      </button>

      <button
        onClick={() => { setMode('choose'); setError(null); }}
        className="text-sm text-ink-3 hover:text-ink-2 transition-colors"
      >
        {t('common.back', 'Back')}
      </button>
    </div>
  );
}
